import React, { useEffect, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

type PayoutDropdownProps = {
  label?: string;
  value: string;
  options?: string[];
  wide?: boolean;
  className?: string;
  buttonClassName?: string;
  onChange?: (value: string) => void;
};

const fallbackOptionsByLabel: Record<string, string[]> = {
  Account: ["All Accounts", "Operating Account", "Salary Account", "Scholarship Account", "Vendor Account"],
  Status: ["All Status", "Active", "Processed", "Processing", "Pending", "Failed", "Inactive"],
  Category: ["All Categories", "Vendor", "Salary", "Scholarship", "Refund", "Reimbursement"],
  Verification: ["All", "Verified", "Pending", "Failed"],
  Role: ["All Roles", "Finance Manager", "Finance Executive", "Administrator", "Observer"],
  "Access Level": ["All Access Levels", "Admin", "Manager", "Executive", "Viewer"],
  Type: ["All", "Credit", "Debit", "System & Custom", "System", "Custom"],
  "Permission Type": ["All Types", "Transaction", "Account", "User Management", "Reports"],
  "Used In": ["All", "Roles", "Users", "Groups"],
  "Requested By": ["All Users", "Finance Manager", "Finance Executive", "Approver"],
  Amount: ["All Amounts", "Below ₹10,000", "₹10,000 - ₹1,00,000", "Above ₹1,00,000"],
  Priority: ["All", "Urgent", "High", "Medium", "Low"],
  "Transaction Type": ["All", "Payout", "Transfer", "Recharge", "Refund"],
  "Credit / Debit": ["All", "Credit", "Debit"],
  "Date Range": ["Today", "Last 7 Days", "This Month", "Last Month", "Custom Range"],
  Date: ["Today", "Yesterday", "Last 7 Days", "This Month"],
  "Applies To": ["All", "Payouts", "Transfers", "Recharges"],
  "Transfer Type": ["All", "NEFT", "RTGS", "IMPS"],
  "From Account Type": ["All", "Operating", "Salary", "Scholarship", "Vendor"],
  "To Account Type": ["All", "Beneficiary", "Internal Account", "Vendor"],
  "Amount Range": ["All", "≤ ₹10,000", "₹10,001 - ₹1,00,000", "Above ₹1,00,000"],
  Time: ["All", "Business Hours", "Non-Business Hours", "Weekend"],
};

const PAYOUT_DROPDOWN_OPEN_EVENT = "payout-dropdown-open";

function getFallbackOptions(label: string | undefined, value: string) {
  const options = label ? fallbackOptionsByLabel[label] : undefined;
  if (!options) return [value, "All", "Active", "Pending"];
  return options.includes(value) ? options : [value, ...options];
}

function PayoutDropdown({
  label,
  value,
  options,
  wide = false,
  className = "",
  buttonClassName = "",
  onChange,
}: PayoutDropdownProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(value);
  const dropdownRef = useRef<HTMLLabelElement>(null);
  const dropdownIdRef = useRef(
    `payout-dropdown-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const resolvedOptions = options ?? getFallbackOptions(label, selected);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  useEffect(() => {
    const handleDocumentPointerDown = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    const handleOtherDropdownOpen = (event: Event) => {
      const openedDropdownId = (event as CustomEvent<string>).detail;
      if (openedDropdownId !== dropdownIdRef.current) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentPointerDown);
    document.addEventListener("touchstart", handleDocumentPointerDown);
    document.addEventListener(PAYOUT_DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleDocumentPointerDown);
      document.removeEventListener("touchstart", handleDocumentPointerDown);
      document.removeEventListener(
        PAYOUT_DROPDOWN_OPEN_EVENT,
        handleOtherDropdownOpen,
      );
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSelect = (option: string) => {
    setSelected(option);
    onChange?.(option);
    setOpen(false);
  };

  return (
    <label
      ref={dropdownRef}
      className={
        "relative block min-w-0 max-w-full " +
        (className ? className : wide ? "min-w-[220px]" : "min-w-[150px]")
      }
    >
      {label && (
        <span className="mb-2 block text-xs font-semibold text-[#505E8E]">
          {label}
        </span>
      )}
      <button
        type="button"
        onClick={() => {
          setOpen((current) => {
            const nextOpen = !current;
            if (nextOpen) {
              document.dispatchEvent(
                new CustomEvent(PAYOUT_DROPDOWN_OPEN_EVENT, {
                  detail: dropdownIdRef.current,
                }),
              );
            }
            return nextOpen;
          });
        }}
        className={
          "flex h-10 w-full items-center justify-between rounded-[6px] border bg-white px-3 text-left text-sm font-semibold text-[#1B163B] " +
          (open ? "border-[#6687FF]" : "border-[#E0E5F2]") +
          " " +
          buttonClassName
        }
      >
        <span className="truncate">{selected}</span>
        <FiChevronDown
          className={
            "ml-3 shrink-0 text-[#505E8E] transition-transform " +
            (open ? "rotate-180" : "")
          }
        />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-[100] mt-1 max-h-64 overflow-y-auto rounded-[8px] border border-[#E0E5F2] bg-white py-1 shadow-[0_14px_28px_rgba(27,22,59,0.12)]">
          {resolvedOptions.map((option) => (
            <button
              type="button"
              key={option}
              onClick={() => handleSelect(option)}
              className={
                "flex w-full items-center justify-between px-3 py-2.5 text-left text-sm font-medium " +
                (option === selected
                  ? "bg-[#6687FF1A] text-[#6687FF]"
                  : "text-[#1B163B] hover:bg-[#A9B2CF1A]")
              }
            >
              <span>{option}</span>
              {option === selected && (
                <span className="h-2 w-2 rounded-full bg-[#6687FF]" />
              )}
            </button>
          ))}
        </div>
      )}
    </label>
  );
}

export default PayoutDropdown;
